"use client";

import FeedbackOutlined from "@mui/icons-material/FeedbackOutlined";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUserRoleOption } from "@/components/user-role-switcher";
import { UserRole } from "@/lib/types";
import { useTranslation } from "react-i18next";
import {LocalizationKeys} from "@/lib/all-localization-keys";

type UserRoleDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function UserRoleDialog({ open, onOpenChange }: UserRoleDialogProps) {
  const { t } = useTranslation();
  const content = useUserRoleOption({
    [UserRole.ALPHA]: () => ({
      title: t(LocalizationKeys.DIALOG_ROLE_ALPHA_TITLE),
      description: t(LocalizationKeys.DIALOG_ROLE_ALPHA_DESC),
      showFeedbackInstructions: true,
    }),
    [UserRole.BETA]: () => ({
      title: t(LocalizationKeys.DIALOG_ROLE_BETA_TITLE),
      description: t(LocalizationKeys.DIALOG_ROLE_BETA_DESC),
      showFeedbackInstructions: true,
    }),
    [UserRole.DEFAULT_USER]: () => null,
    unauthenticated: () => null,
  })();

  if (!content) {
    return null;
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader className="space-y-4">
          <DialogTitle className="font-semibold text-xl">
            {content.title}
          </DialogTitle>
          <DialogDescription className="whitespace-pre-line text-base text-foreground/80 leading-relaxed">
            {content.description}
          </DialogDescription>
        </DialogHeader>

        {content.showFeedbackInstructions && (
          <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
            <h3 className="font-semibold text-foreground text-sm">
              {t(LocalizationKeys.DIALOG_ROLE_FEEDBACK_HEADER)}
            </h3>
            <p className="text-foreground/70 text-sm leading-relaxed">
              {t(LocalizationKeys.DIALOG_ROLE_FEEDBACK_INTRO)}
            </p>
            <div className="flex items-center gap-2 rounded-md bg-background px-3 py-2">
              <FeedbackOutlined
                className="text-foreground/60"
                sx={{ fontSize: 16 }}
              />
              <span className="text-foreground/70 text-sm">
                {t(LocalizationKeys.DIALOG_ROLE_FEEDBACK_ICON_LABEL)}
              </span>
            </div>
            <p className="text-foreground/70 text-sm leading-relaxed">
              {t(LocalizationKeys.DIALOG_ROLE_FEEDBACK_HELP)}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} type="button">
            {t(LocalizationKeys.BUTTON_OKAY)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
